import { getQuickQuote } from '@/api/quotes';
import {
  GLOBAL_ERROR,
  INITIAL_APPLICATION,
  INITIAL_QUICK_QUOTE,
  ROUTES_REQUIRING_COMPLETED_FORMS,
} from '@/utils/constants';
import { getEnv } from 'ecx.saigon.ui.common';
import _get from 'lodash/get';
import Vue from 'vue';
import {
  clearApplication as clearApplicationCall,
  getApplication as getApplicationCall,
  sendHRApproval as sendHRApprovalCall,
  updateApplication as updateApplicationCall,
} from '../../api/application';
import {
  createFileApi,
  getFileApi,
  postFileToSignedUrlApi,
} from '../../api/files';
import getApplicationForUpdate from '../../utils/get-application-for-update';
import safeSetApplication from '../../utils/safe-set-application';
import deepPageValidation from '../../utils/validations/deep-page-validations';

export async function updateApplication(
  { commit, dispatch, getters, state },
  { updateApplicationPaths: updatePaths, disableSaleForceUpdate, quoteFile }
) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('ui/setDisableNavigation', true, { root: true });
      commit('setApplicationSaving', true);
      const payload = updatePaths
        ? getApplicationForUpdate(state.application, updatePaths)
        : state.application;
      let financeBudgetPayload = getters['financeBudgetPayload']();
      let application = await updateApplicationCall({
        metaData: {
          ...(state.application.metaData.costsWeekly && {
            costsWeekly: state.application.metaData.costsWeekly,
          }),
          ...(payload.metaData.customerOptions
            ? {
                customerOptions: payload.metaData.customerOptions,
              }
            : financeBudgetPayload && {
                customerOptions: {
                  annualSalary: financeBudgetPayload.customer.annualSalary,
                  carValue: financeBudgetPayload.price,
                  vehicleCondition: financeBudgetPayload.vehicleCondition,
                  bodyType: financeBudgetPayload.bodyType,
                  leaseTerm: financeBudgetPayload.leaseTerm,
                  kmsPerYear: financeBudgetPayload.kmsPerYear,
                  businessUse: financeBudgetPayload.businessUse,
                },
              }),
        },
        disableSaleForceUpdate,
        ...(state.leaseId && { contractId: state.leaseId }),
      });
      application = safeSetApplication(state.application, application);
      commit('setApplication', application);
      quoteFile && commit('updateQuoteUpload', { field: 'quoteFile', value: quoteFile });
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setApplicationSaving', false);
      return resolve();
    } catch (err) {
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setApplicationSaving', false);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        action: () => dispatch('updateApplication'),
      });
      return reject();
    }
  });
}

export async function getApplication({ commit, dispatch, state }) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('setApplicationLoading', true);
      let application = await getApplicationCall();
      application = safeSetApplication(state.application, application);
      if (application.applicationId) {
        commit('setApplication', application);
      } else {
        const hasCustomerOptionFromLastOpportunity = !!_get(
          application,
          'metaData.customerOptions.leaseTerm'
        );
        if (hasCustomerOptionFromLastOpportunity) {
          commit('setCustomerOptions', application.metaData.customerOptions);
        }
      }
      commit('setApplicationLoading', false);
      return resolve();
    } catch (err) {
      commit('setApplicationLoading', false);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        action: () => dispatch('getApplication'),
      });
      return reject();
    }
  });
}

// Calculation is done only if:
// 1. No calculation done before only if vehicle-application exists (ie. calculator was previously populated), OR
// 2. There is a change to calculator, OR
// 3. If no quote has been created
export async function getBudgetData({ commit, dispatch, getters }) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('setFinanceLoading', true);
      commit('ui/setDisableNavigation', true, { root: true });
      let payload = getters['financeBudgetPayload']();
      let response = await getQuickQuote(payload);
      response = response || { ...INITIAL_QUICK_QUOTE };
      commit('setCostsWeekly', response.costs);
      commit('setQuickQuote', response);
      return resolve(response);
    } catch (err) {
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
        // action: () => dispatch('getFinanceData'),
        action: () => dispatch('getBudgetData'),
      });
      return reject();
    } finally {
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setFinanceLoading', false);
    }
  });
}

export async function saveQuoteFile({ commit, dispatch, state }, file) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('ui/setDisableNavigation', true, { root: true });
      commit('setSavingQuoteFile', true);
      let newFile;
      if (!file.uuid) {
        let splitName = file.name.split('.');
        let name = splitName[0];
        let extension = splitName[1];
        // Get signed URL
        let createDetails = await createFileApi(name, extension, file.type);
        // Upload the photo to GCS
        await postFileToSignedUrlApi(createDetails.signedUrl, file);
        // Get the downloadable signed URL
        let fileDetails = await getFileApi(createDetails.uuid);

        newFile = {
          uuid: createDetails.uuid,
          name: `${fileDetails.fileName}.${fileDetails.fileExtension}`,
          extension: fileDetails.fileExtension,
          href: fileDetails.signedUrl,
        };
      }

      commit('updateQuoteUpload', { field: 'quoteFile', value: newFile });
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setSavingQuoteFile', false);
      return resolve();
    } catch (err) {
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setSavingQuoteFile', false);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
      });
      return reject();
    }
  });
}

export async function getQuoteFile({ commit, state }, quoteFilePath) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('setGettingQuoteFile', true);
      let file;
      if (quoteFilePath.uuid) {
        let fileDetails = await getFileApi(quoteFilePath.uuid);
        file = {
          name: `${fileDetails.fileName}.${fileDetails.fileExtension}`,
          extension: fileDetails.fileExtension,
          href: fileDetails.signedUrl,
        };
      }

      commit('updateQuoteUpload', { field: 'quoteFile', value: file });
      commit('setGettingQuoteFile', false);
      return resolve();
    } catch (err) {
      commit('setGettingQuoteFile', false);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
      });
      return reject();
    }
  });
}

export async function sendHRApprovalRequest({ commit, state }) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('ui/setDisableNavigation', true, { root: true });
      commit('setSendingHRApproval', true);
      await sendHRApprovalCall();
      commit('setApplication', {
        ...state.application,
        metaData: {
          ...state.application.metaData,
          hrApprovalSent: true,
        },
      });
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setSendingHRApproval', false);
      return resolve();
    } catch (err) {
      commit('ui/setDisableNavigation', false, { root: true });
      commit('setSendingHRApproval', false);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
      });
      return reject();
    }
  });
}

export async function clearApplicationRequest({ commit }) {
  return new Promise(async (resolve, reject) => {
    try {
      commit('setClearingApplication', true);
      await clearApplicationCall();
      commit('setApplication', INITIAL_APPLICATION);
      commit('carSearch/resetSelected', undefined, { root: true });
      commit('setClearingApplication', false);
      return resolve();
    } catch (err) {
      commit('setClearingApplication', false);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
      });
      return reject();
    }
  });
}

export async function saveAndExitApplication(
  { commit, state, dispatch },
  route
) {
  return new Promise(async (resolve, reject) => {
    if (!route) {
      return resolve();
    }
    try {
      const env = await getEnv();
      const coreRoute = env.VUE_APP_CORE_ROUTE;

      if (!state.application.applicationId) {
        window.location = coreRoute;
        return resolve();
      }

      const updatePaths = ROUTES_REQUIRING_COMPLETED_FORMS[route];
      if (updatePaths && updatePaths.length) {
        if (deepPageValidation(route)) {
          commit('setSavingForExit', true);
          await dispatch('updateApplication', updatePaths);
          commit('setSavingForExit', false);
          window.location = coreRoute;
          return resolve();
        } else {
          Vue.prototype.SAIDialog({
            body: 'This page must be completed before it can be saved.',
            okText: 'Ok',
          });
          return resolve();
        }
      } else {
        window.location = coreRoute;
        return resolve();
      }
    } catch (err) {
      commit('setSavingForExit', false);
      Vue.prototype.$euiNotifications.emit({
        notification: GLOBAL_ERROR,
      });
      return reject();
    }
  });
}
